'use client'

// ============================================================
// components/cross-sell-widget.tsx
// ============================================================
// Widget de cross-sell reutilizavel.
//
// Consome /api/v1/recommend (via RecommendationClient internamente, mas
// como esse endpoint exige API key e nao deve ser chamado do browser, usa
// o proxy /api/cross-sell/recommend que injeta a key server-side).
//
// Variantes visuais:
// - cards: 1 a 3 cards verticais (default)
// - list: linha simples
// - carousel: scroll horizontal (mobile-friendly)
//
// Tracking:
// - Impressoes: 1 fetch POST /api/cross-sell/track 'shown' por reco mostrada
// - Cliques: idem com action=clicked antes de navegar
// ============================================================

import { useEffect, useRef, useState } from 'react'

export type CrossSellContext =
  | 'after_purchase'
  | 'on_dashboard'
  | 'in_checkout'
  | 'on_blog_post'

export type CrossSellVariant = 'cards' | 'list' | 'carousel'

export interface CrossSellWidgetProps {
  /** UUID do user (server passa). Se vazio, widget renderiza nada. */
  userId?: string
  /** Contexto - mapeia pra rule no admin */
  context: CrossSellContext
  /** Max items renderizados (default 3) */
  maxItems?: number
  /** Layout visual (default cards) */
  variant?: CrossSellVariant
  /** App slug (default 'ebuz') */
  app?: string
  /** Titulo opcional acima do widget */
  title?: string
  /** Classe CSS extra no container */
  className?: string
}

interface ApiRecommendation {
  rule_id: string
  recommendation: {
    type: string
    id: string
    cta?: string
    metadata?: {
      title?: string
      description?: string
      image_url?: string
      url?: string
      price_cents?: number
    } & Record<string, unknown>
  }
}

interface ApiResponse {
  recommendations?: ApiRecommendation[]
}

export function CrossSellWidget({
  userId,
  context,
  maxItems = 3,
  variant = 'cards',
  app = 'ebuz',
  title,
  className = '',
}: CrossSellWidgetProps) {
  const [recommendations, setRecommendations] = useState<ApiRecommendation[]>([])
  const [loading, setLoading] = useState(true)
  const trackedImpressions = useRef<Set<string>>(new Set())

  useEffect(() => {
    let cancel = false
    // Note: userId e opcional. Quando ausente, o proxy /api/cross-sell/recommend
    // resolve via cookie de sessao. Quando presente, server passa pra evitar
    // round-trip extra.
    ;(async () => {
      try {
        const params = new URLSearchParams({
          context,
          app,
          limit: String(maxItems),
        })
        const res = await fetch(`/api/cross-sell/recommend?${params.toString()}`, {
          credentials: 'same-origin',
        })
        if (!res.ok) {
          if (!cancel) {
            setRecommendations([])
            setLoading(false)
          }
          return
        }
        const json = (await res.json()) as ApiResponse
        if (!cancel) {
          setRecommendations(json.recommendations ?? [])
          setLoading(false)
        }
      } catch {
        if (!cancel) {
          setRecommendations([])
          setLoading(false)
        }
      }
    })()
    return () => {
      cancel = true
    }
  }, [userId, context, app, maxItems])

  // Track impression apos render
  useEffect(() => {
    for (const r of recommendations) {
      if (trackedImpressions.current.has(r.rule_id)) continue
      trackedImpressions.current.add(r.rule_id)
      void trackInteraction(r.rule_id, 'shown')
    }
  }, [recommendations])

  if (loading || recommendations.length === 0) return null

  const items = recommendations.slice(0, maxItems)

  return (
    <section
      className={`cross-sell-widget ${className}`}
      data-context={context}
      data-variant={variant}
      aria-label="Recomendacoes para voce"
    >
      {title && (
        <h2 className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-3">
          {title}
        </h2>
      )}

      {variant === 'cards' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((r) => (
            <CrossSellCard key={r.rule_id} reco={r} onClick={() => trackInteraction(r.rule_id, 'clicked')} />
          ))}
        </div>
      )}

      {variant === 'list' && (
        <ul className="space-y-2">
          {items.map((r) => (
            <li key={r.rule_id}>
              <CrossSellListItem reco={r} onClick={() => trackInteraction(r.rule_id, 'clicked')} />
            </li>
          ))}
        </ul>
      )}

      {variant === 'carousel' && (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory">
          {items.map((r) => (
            <div key={r.rule_id} className="snap-start shrink-0 w-72">
              <CrossSellCard reco={r} onClick={() => trackInteraction(r.rule_id, 'clicked')} />
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// ----- subcomponents -----

function CrossSellCard({
  reco,
  onClick,
}: {
  reco: ApiRecommendation
  onClick: () => void
}) {
  const meta = reco.recommendation.metadata ?? {}
  const url = String(meta.url ?? '#')
  const titleText = String(meta.title ?? reco.recommendation.id)
  const description = meta.description ? String(meta.description) : null
  const cta = reco.recommendation.cta ?? 'Ver detalhes'
  const image = meta.image_url ? String(meta.image_url) : null

  return (
    <a
      href={url}
      onClick={onClick}
      className="block bg-white border border-stone-200 rounded-xl overflow-hidden hover:border-blue-400 hover:shadow-md transition-all group"
    >
      {image && (
        <div className="aspect-[16/9] bg-stone-100 overflow-hidden">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={image}
            alt={titleText}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          />
        </div>
      )}
      <div className="p-4">
        <h3 className="text-sm font-bold text-stone-900 mb-1 line-clamp-2">
          {titleText}
        </h3>
        {description && (
          <p className="text-xs text-stone-500 mb-3 line-clamp-2">{description}</p>
        )}
        <span className="text-xs font-semibold text-blue-600 group-hover:text-blue-800">
          {cta} &gt;
        </span>
      </div>
    </a>
  )
}

function CrossSellListItem({
  reco,
  onClick,
}: {
  reco: ApiRecommendation
  onClick: () => void
}) {
  const meta = reco.recommendation.metadata ?? {}
  const url = String(meta.url ?? '#')
  const titleText = String(meta.title ?? reco.recommendation.id)
  const cta = reco.recommendation.cta ?? 'Ver'

  return (
    <a
      href={url}
      onClick={onClick}
      className="flex items-center justify-between bg-white border border-stone-200 rounded-lg px-4 py-3 hover:border-blue-400 group"
    >
      <span className="text-sm font-medium text-stone-900">{titleText}</span>
      <span className="text-xs font-semibold text-blue-600 group-hover:text-blue-800">
        {cta} &gt;
      </span>
    </a>
  )
}

// ----- tracking -----

async function trackInteraction(
  ruleId: string,
  action: 'shown' | 'clicked' | 'purchased',
): Promise<void> {
  try {
    await fetch('/api/cross-sell/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ rule_id: ruleId, action }),
      keepalive: true, // sobrevive a navegacao on click
    })
  } catch {
    // best effort, sem bloquear UX
  }
}
